import z from 'zod';
/**
 * The project's No Incident/Accident Statements (DR-3125). One webhook carries
 * EVERY statement in the project — successive operators and successive coverage
 * periods are all part of the engine's record — so the envelope mirrors
 * `bill-of-sale-events-ready`: a project id plus a flat array whose rows each
 * name their own source document / reconstructed statement.
 *
 * Every business field is optional. An NIS is a free-form letter, not a form, and
 * the extraction is a single call: a statement that never names its operator, or
 * a signature page that carries only the signed flag, is a real document and must
 * still be delivered. The two booleans are tri-state (`true` / `false` / absent)
 * so a consumer can tell a declared "no incidents" apart from an unstated one.
 */
export const nisEventsReadyWebhookSchema = z.looseObject({
    type: z.literal('nis-events-ready'),
    projectId: z.string(),
    // `z.iso.datetime()`, not a bare string: the consumer contract is an ORDERABLE instant,
    // so the schema enforces what the field documents. Follows `pma-der-events-ready` /
    // `document-header-ready` rather than the looser `project-progress` / `project-data-ready`
    // spelling.
    //
    // Required. Brain always emits this stamp. An envelope without it does not parse (DR-3341).
    generatedAt: z.iso.datetime(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    nisEvents: z.array(z.looseObject({
        statementDate: z.string().optional(),
        coveragePeriodStart: z.string().optional(),
        coveragePeriodEnd: z.string().optional(),
        operatorName: z.string().optional(),
        issuer: z.string().optional(),
        // The issuer may be the operator itself, a CAMO acting on its behalf, or a `custodian` that
        // certifies only that it held the engine (possession / custody / control, not operation;
        // DR-4623) — all legitimate NIS issuers, unlike the operator-metadata lane. Brain holds
        // `custodian` back from this webhook (the row omits `issuerRole`) until DR-4626 ships it
        // in Oil and v2; consumers must accept `custodian` once that is live.
        issuerRole: z.enum(['operator', 'camo_on_behalf', 'custodian']).optional(),
        esn: z.string().optional(),
        // DR-4329: the letter covers the engines of an ATTACHED list/schedule and prints no serial
        // for them, so an absent `esn` on this row is by design. Brain sets it only on such a
        // serial-less letter; a consumer must still tolerate `esn` and this flag together and must
        // not read the flag as "this row names no engine". Only ever `true` or absent.
        coversAttachedEngineList: z.boolean().optional(),
        // DR-5210: the statement says the covered period starts when the engine was new ('from
        // New', 'since new', 'since manufacture'); `coveragePeriodStart` is then absent — a row never
        // carries both. `coveragePeriodEnd` carries the stated end, often the statement date. Only
        // ever `true` or absent. Brain emits it on NIS rows now; PMA/DER rows emit it under DR-5211.
        coverageSinceNew: z.boolean().optional(),
        noIncidentDeclaration: z.boolean().optional(),
        isSigned: z.boolean().optional(),
        // The engine's hours / cycles at the two boundaries of the covered period (DR-4025),
        // read only from a value the page CAPTIONS as that boundary. ABSENT means the page
        // printed no such value — the family's optional-not-null contract, the same one every
        // business field above carries; the row never sends a null in their place.
        coverageStartTsn: z.number().nonnegative().optional(),
        coverageStartCsn: z.number().nonnegative().optional(),
        coverageEndTsn: z.number().nonnegative().optional(),
        coverageEndCsn: z.number().nonnegative().optional(),
        // DR-4622: what the certifying declaration is about. `aircraft` means the coverage period is
        // the AIRCRAFT's and this engine row is one of its installed engines; absent means not
        // determined. The registration and MSN are sent only on an `aircraft` row, verbatim.
        statementSubject: z.enum(['aircraft', 'engine']).optional(),
        aircraftRegistration: z.string().optional(),
        aircraftMsn: z.string().optional(),
        /** Representative live document of the statement's reconstructed entity. */
        documentId: z.string(),
        /** Stable id of the reconstructed statement (declaration + signature pages). */
        reconstructedDocumentId: z.string(),
        /** Every composing page of the statement, in reconstructed order. */
        pageIds: z.array(z.string()),
        /** The single page the row's fields were extracted from — evidence deep-link. */
        pageId: z.string(),
    })),
});
//# sourceMappingURL=nis-events-ready.webhook.js.map